import React, { useState } from 'react';
import { Card, Segmented, Statistic, Row, Col, Table, Empty, Skeleton, Tag, Button, Dropdown } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, DownloadOutlined } from '@ant-design/icons';
import { useCurrency } from '../../../hooks/useCurrency';
import { useGetCashFlowReportQuery } from '../../../services/reportsApi';
import { useAppSelector } from '../../../hooks/redux';
import { exportCSV, exportExcel, exportPDF, getPeriodLabel, ExportConfig } from '../../../utils/reportExport';
import { useRestaurantTimeZone } from '../../../hooks/useTimeZone';

interface CashFlowAnalysisProps {
  restaurantId: string;
  fromDate: string;
  toDate: string;
  selectedPeriod?: string;
  customDateRange?: [any, any] | null;
}

const TYPE_LABELS: Record<string, string> = {
  SALE: 'Ventas Efectivo',
  CREDIT_CARD_SALE: 'Ventas T. Credito',
  DEBIT_CARD_SALE: 'Ventas T. Debito',
  TRANSFER_SALE: 'Transferencias',
  OTHER_SALE: 'Otros Pagos',
  TIP: 'Propinas',
  DROP: 'Retiros',
  EXPENSE: 'Gastos',
  PAYOUT: 'Pagos',
  INITIAL: 'Fondo Inicial',
  CORRECTION: 'Correcciones',
  OVERAGE: 'Sobrantes',
  SHORTAGE: 'Faltantes',
  CASH_SALE: 'Ventas Efectivo',
  CARD_SALE: 'Ventas Tarjeta',
};

const SEGMENTED_OPTIONS = [
  { label: 'Dia', value: 'day' },
  { label: 'Semana', value: 'week' },
  { label: 'Mes', value: 'month' },
];

const CashFlowAnalysis: React.FC<CashFlowAnalysisProps> = ({ restaurantId, fromDate, toDate, selectedPeriod, customDateRange }) => {
  const [groupBy, setGroupBy] = useState<string>('day');
  const { format } = useCurrency();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { formatInTimeZone } = useRestaurantTimeZone();

  const { data, isLoading } = useGetCashFlowReportQuery(
    { restaurantId, fromDate, toDate, groupBy },
    { skip: !restaurantId || !fromDate || !toDate }
  );

  if (isLoading) {
    return (
      <Card title="Flujo de Efectivo">
        <Skeleton active />
      </Card>
    );
  }

  if (!data || data.periods.length === 0) {
    return (
      <Card title="Flujo de Efectivo">
        <Segmented
          options={SEGMENTED_OPTIONS}
          value={groupBy}
          onChange={(val) => setGroupBy(val as string)}
          style={{ marginBottom: 16 }}
        />
        <Empty description="No hay datos para el periodo seleccionado" />
      </Card>
    );
  }

  const { periods, totalInflows, totalOutflows, netFlow, inflowsByType, outflowsByType } = data;
  const maxValue = Math.max(...periods.map((p) => Math.max(p.inflows, p.outflows)), 1);

  const netFlowColor = netFlow >= 0 ? 'var(--color-success-text)' : 'var(--color-error-text)';

  const inflowRows = Object.entries(inflowsByType).map(([type, amount]) => ({
    key: `in-${type}`,
    tipo: TYPE_LABELS[type] || type,
    monto: amount,
    flow: 'inflow' as const,
  }));

  const outflowRows = Object.entries(outflowsByType).map(([type, amount]) => ({
    key: `out-${type}`,
    tipo: TYPE_LABELS[type] || type,
    monto: amount,
    flow: 'outflow' as const,
  }));

  const tableColumns = [
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      key: 'monto',
      render: (value: number, record: { flow: string }) => (
        <Tag color={record.flow === 'inflow' ? 'green' : 'red'}>{format(value)}</Tag>
      ),
    },
  ];

  const handleExportGrid = (fmt: 'csv' | 'excel' | 'pdf') => {
    const periodLabel = getPeriodLabel(selectedPeriod || 'today', customDateRange);
    const config: ExportConfig = {
      reportTitle: 'Flujo de Efectivo',
      periodLabel,
      restaurantName: currentRestaurant?.name || 'N/A',
      timestamp: formatInTimeZone(new Date(), 'DD/MM/YYYY HH:mm'),
      summaryCards: [
        { label: 'Total Entradas', value: format(totalInflows) },
        { label: 'Total Salidas', value: format(totalOutflows) },
        { label: 'Flujo Neto', value: format(netFlow) },
      ],
      sections: [
        {
          title: 'Periodos',
          titleStyle: 'cash',
          headers: ['Periodo', 'Entradas', 'Salidas', 'Flujo Neto'],
          rows: periods.map((p) => [
            p.periodLabel,
            format(p.inflows),
            format(p.outflows),
            format(p.inflows - p.outflows),
          ]),
        },
        {
          title: 'Entradas por Tipo',
          titleStyle: 'default',
          headers: ['Tipo', 'Monto'],
          rows: inflowRows.map((r) => [r.tipo, format(r.monto)]),
        },
        {
          title: 'Salidas por Tipo',
          titleStyle: 'section',
          headers: ['Tipo', 'Monto'],
          rows: outflowRows.map((r) => [r.tipo, format(r.monto)]),
        },
      ],
    };

    if (fmt === 'csv') exportCSV(config, 'flujo-efectivo');
    else if (fmt === 'excel') exportExcel(config, 'flujo-efectivo');
    else exportPDF(config);
  };

  const exportMenuItems = [
    { key: 'csv', label: 'CSV', onClick: () => handleExportGrid('csv') },
    { key: 'excel', label: 'Excel', onClick: () => handleExportGrid('excel') },
    { key: 'pdf', label: 'PDF', onClick: () => handleExportGrid('pdf') },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Entradas"
              value={totalInflows}
              precision={2}
              valueStyle={{ color: 'var(--color-success-text)' }}
              prefix={<ArrowUpOutlined />}
              formatter={() => format(totalInflows)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Salidas"
              value={totalOutflows}
              precision={2}
              valueStyle={{ color: 'var(--color-error-text)' }}
              prefix={<ArrowDownOutlined />}
              formatter={() => format(totalOutflows)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Flujo Neto"
              value={netFlow}
              precision={2}
              valueStyle={{ color: netFlowColor }}
              prefix={netFlow >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
              formatter={() => format(netFlow)}
            />
          </Card>
        </Col>
      </Row>

      <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Segmented
          options={SEGMENTED_OPTIONS}
          value={groupBy}
          onChange={(val) => setGroupBy(val as string)}
        />
        <Dropdown menu={{ items: exportMenuItems }}>
          <Button icon={<DownloadOutlined />} size="small">
            Exportar este grid
          </Button>
        </Dropdown>
      </div>

      {/* CSS Bar Chart */}
      <Card size="small" style={{ marginBottom: 24 }}>
        {/* Legend */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 12, height: 12, backgroundColor: '#52c41a', borderRadius: 2 }} />
            <span>Entradas</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 12, height: 12, backgroundColor: '#ff4d4f', borderRadius: 2 }} />
            <span>Salidas</span>
          </div>
        </div>

        {periods.map((period) => (
          <div key={period.periodLabel} style={{ marginBottom: 8 }}>
            <div style={{ marginBottom: 4, fontSize: 12 }}>{period.periodLabel}</div>
            <div style={{ display: 'flex', gap: 4, height: 24 }}>
              <div
                style={{
                  width: `${(period.inflows / maxValue) * 100}%`,
                  backgroundColor: '#52c41a',
                  borderRadius: 4,
                  minWidth: period.inflows > 0 ? 4 : 0,
                }}
              />
              <div
                style={{
                  width: `${(period.outflows / maxValue) * 100}%`,
                  backgroundColor: '#ff4d4f',
                  borderRadius: 4,
                  minWidth: period.outflows > 0 ? 4 : 0,
                }}
              />
            </div>
          </div>
        ))}
      </Card>

      {/* Breakdown tables */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Card size="small" title="Entradas">
            <Table
              columns={tableColumns}
              dataSource={inflowRows}
              pagination={false}
              size="small"
              locale={{ emptyText: 'Sin entradas' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card size="small" title="Salidas">
            <Table
              columns={tableColumns}
              dataSource={outflowRows}
              pagination={false}
              size="small"
              locale={{ emptyText: 'Sin salidas' }}
            />
          </Card>
        </Col>
      </Row>
    </Card>
    </div>
  );
};

export default CashFlowAnalysis;
